import React, { useEffect, useRef, useState } from 'react';
import { compareAsc, format, parse } from 'date-fns';
import useTealium from '../../../../../hooks/useTealium';
import styles from './NotesTable.scss';
import checkDate from 'status/utilities/checkDate';
import NotesSearch from './NotesSearch';
import DownloadNoteReportCell from './DownloadNoteReportCell';
import useReportDownload from './useReportDownload';
import { Alert, Button, Card, CompositeTable, DisplayField, Icon, Spinner, themes } from '@digitools/honest-react';
import styled from 'styled-components';
import { TEALIUM_EVENT } from '../../../../../constants/tealiumConstants';
import { CardIcon } from '@digitools/honest-react/dist/lib/components/Card';
import { StatusMessage } from '../CoverageDetails/ClaimsAndLeaves';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import useTranslate from 'src/hooks/useTranslate';
import { NOTE_TEXT } from './NoteText';
import { compareByField } from 'src/utils/SortUtils';
/**
 * TODO: (later) Should maybe exist in honest with other Card sub-components alongside CardLink - copied that style and made it a button for ADA
 */
export const CardButton = styled('button') `
  background: none;
  border: none;
  color: ${({ color, theme }) => color
    ? (theme?.palette?.[color] ?? themes.default.palette[color])
    : (theme?.link?.color ?? themes.default.link.color)};
  text-decoration: none;
  text-align: left;
  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
  display: flex;
  gap: .25rem;
`;
const NoteDiv = styled('div') `
  background-color: white;
  width: 99%;
  margin-right: 1rem;
  margin-left: -0.8125rem;
  margin-bottom: 1rem;
  padding-left: 3rem;
`;
const ExpandCollapseCell = ({ data }) => {
    const { trackEvent } = useTealium();
    const noteRef = useRef(null);
    const [height, setHeight] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const toggleExpand = () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.NOTE_TOGGLE_EXPAND,
            event_name: 'Note Toggle Expand',
        });
        setExpanded(!expanded);
    };
    const setNoteHeight = () => {
        setHeight(noteRef?.current?.clientHeight);
    };
    useEffect(() => {
        setHeight(noteRef?.current?.clientHeight);
        window.addEventListener('resize', setNoteHeight);
        return () => window.removeEventListener('resize', setNoteHeight);
    });
    return (<div className={styles.noteWrapper} style={{ paddingBottom: `calc(${height}px + 0.5rem)` }} data-expanded={expanded}>
      <Button onClick={toggleExpand} size={'small'} className={styles.icon}>
        {expanded ? <Icon name='subtract' color={'primary'}/> : <Icon name='add' color={'primary'}/>}
      </Button>
      {data._original.noteType}
      <NoteDiv data-private={true} ref={noteRef} className={styles.noteRow} data-expanded={expanded}>
        <p>{data._original.noteTxt}</p>
      </NoteDiv>
    </div>);
};
/**
 * NotesTab
 * Takes in notes data, error and loading state, along with basic details needed for APIs
 * Renders notes and allows downloading of individual notes, or for ERs, downloading all notes
 *
 * ER - renders notes table
 * EE - renders notes as cards
 */
const NotesTab = ({ notes, error, loading, noteBasicDetails }) => {
    const [filteredData, setFilteredData] = useState([]);
    const { experience } = useExperience();
    const { sendRequest } = useReportDownload();
    const { trackEvent } = useTealium();
    const { t } = useTranslate();
    //TODO: not in story - download loading state spinnder/disable for note download
    if (loading) {
        return <Spinner id='notes-table-spinner'/>;
    }
    if (error) {
        return <Alert type={'error'}>There was a problem retrieving notes.</Alert>;
    }
    if (!notes || notes.length === 0) {
        return <Alert type={'info'}>No notes found for this employee.</Alert>;
    }
    const formatDataForDisplay = (note) => {
        return {
            ...note,
            dateCreated: checkDate(format(parse(note.dateCreated, 'yyyy-MM-dd', new Date()), 'P')),
            lastModified: checkDate(format(parse(note.lastModified, 'yyyy-MM-dd', new Date()), 'P')),
        };
    };
    let formattedData;
    if (filteredData.length !== 0) {
        formattedData = filteredData.map(data => formatDataForDisplay(data));
    }
    else {
        formattedData = notes.map(note => formatDataForDisplay(note));
    }
    const columns = [
        {
            Cell: ExpandCollapseCell,
            Header: 'Note type',
            id: 'expandCollapse',
            accessor: 'noteType',
        },
        {
            Header: 'Topic',
            accessor: 'noteTopic',
            id: 'noteTopic',
        },
        {
            Header: 'Sub topic',
            accessor: 'noteSubTopic',
            id: 'noteSubTopic',
        },
        {
            Header: 'Date created',
            accessor: 'dateCreated',
            id: 'dateCreated',
            sortMethod: (a, b) => compareAsc(parse(a, 'P', new Date()), parse(b, 'P', new Date())),
        },
        {
            Header: 'Last modified',
            accessor: 'lastModified',
            id: 'lastModified',
            sortMethod: (a, b) => compareAsc(parse(a, 'P', new Date()), parse(b, 'P', new Date())),
        },
        {
            Cell: DownloadNoteReportCell(noteBasicDetails),
            Header: '',
            id: 'download',
        },
    ];
    const defaultSorted = [
        {
            id: 'dateCreated',
            desc: true,
        },
    ];
    const downloadAllNotes = async () => {
        //notes report flips the order, have to make it ascending so it prints descending
        formattedData.sort((a, b) => b.noteNum - a.noteNum);
        //TODO: update when S1 sends us timestamp so dateCreated can be used.
        // formattedData.sort((a: INote, b: INote) =>
        //   compareDesc(parse(a.dateCreated, 'P', new Date()), parse(b.dateCreated, 'P', new Date())),
        // ); 
        const noteType = formattedData.map(note => { return note.noteType.toLowerCase(); });
        const payload = { ...noteBasicDetails, notesResourceList: formattedData };
        const url = '/status-service/report/notes/download';
        await sendRequest(url, payload);
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.DOWNLOAD,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_name: `download all - ${noteType[0]} notes`,
            event_version: `${noteType[0]} details`,
        });
    };
    const downloadNote = async (noteBasicDetails, note) => {
        const payload = { ...noteBasicDetails, notesResourceList: [note] };
        const url = `/status-service/report/notes/download`;
        // setDownloading(nodeId); //can optionally add in loading state/behavior later
        await sendRequest(url, payload);
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.DOWNLOAD,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_name: 'download - status update',
            event_version: `${note.noteType.toLowerCase()} details`,
        });
    };
    /** Always sort EE note cards by dateCreated */
    if (experience === Experience.EMPLOYEE) {
        formattedData.sort(compareByField('dateCreated', false));
    }
    else {
        // Create default initial sort by NoteNum descending. 
        //TODO: update when S1 sends us timestamp so dateCreated can be used.
        formattedData.sort((a, b) => a.noteNum - b.noteNum);
    }
    return (<div>
      {experience === Experience.EMPLOYER && (<>
          <NotesSearch initialData={notes} download={downloadAllNotes} setFilteredData={setFilteredData}/>
          {formattedData.length !== 0 && (<CompositeTable data-testid='notes-table' data={formattedData} defaultSorted={defaultSorted} columns={columns} paginated={formattedData.length > 10}/>)}
          {formattedData.length === 0 && <Alert type={'info'}>No notes found.</Alert>}
        </>)}

      {experience === Experience.EMPLOYEE &&
            formattedData.map((note, i) => (<Card data-testid='note-card' borderColor='accent1Darker' cardLinks={<CardButton onClick={() => {
                        downloadNote(noteBasicDetails, note);
                    }}>
                <CardIcon name='download' color={'secondaryDarker'}></CardIcon>Download
              </CardButton>}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <DisplayField style={{ minWidth: '4rem', flex: '1 1 5%' }} label={t(NOTE_TEXT.noteType)} value={note.noteType}/>
              <DisplayField style={{ minWidth: '12rem', flex: '1 1 10%' }} label={t(NOTE_TEXT.topic)} value={note.noteTopic}/>
              <DisplayField style={{ minWidth: '12rem', flex: '1 1 25%' }} label={t(NOTE_TEXT.subTopic)} value={note.noteSubTopic?.trim()}/>
              <DisplayField data-testid='note-date-created' style={{ minWidth: '8rem', flex: '1 1 5%' }} label={t(NOTE_TEXT.dateCreated)} value={note.dateCreated}/>
              <DisplayField style={{ minWidth: '8rem', flex: '1 1 5%' }} label={t(NOTE_TEXT.lastModifiedDate)} value={note.lastModified}/>
            </div>
            <div>
              <StatusMessage data-private={true} style={{ color: `${themes.default.palette.black}`, textTransform: 'uppercase', marginRight: '0', marginLeft: '0' }}>
                <Icon size='small' name='important-message' color='primaryDarker' className='ml-2 mr-2'/>
                {note.noteTxt}
              </StatusMessage>
            </div>
          </Card>))}
    </div>);
};
export default NotesTab;
